import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Grid, Chip, Tooltip } from '@mui/material';
import { Close, Visibility } from '@mui/icons-material';
import { headingBlue, red, lightGrey, mediumGrey } from '../../../assets/colors';
import { buttonStyle } from '../../../utils/commonCSS';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../CustomComponents/CustomComponentsSlice';
import Loader from '../../Loader';
import HttpService from '../../../backend/requestApis';
import { fetchBatchAssessmentResults } from '../../../backend/backendApis';
import moment from 'moment';

const MemberHistoryDialog = ({ open, onClose, memberName, attempts }) => {
    const headerCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.85rem", 
        fontWeight: "600",
        textAlign: 'center',
        backgroundColor: lightGrey,
        borderBottom: `1px solid ${mediumGrey}`,
        whiteSpace: 'nowrap'
    };

    const bodyCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.85rem", 
        fontWeight: "400",
        textAlign: 'center',
    };

    return (
        <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
            <DialogTitle style={{ backgroundColor: headingBlue, color: 'white', fontSize: '1.05rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 1rem' }}>
                <Typography style={{ fontWeight: 600 }}>Attempt History: {memberName}</Typography>
                <IconButton onClick={onClose} style={{ color: 'white', padding: 0 }}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box style={{ padding: '1.5rem 0rem 0rem 0rem' }}>
                    {(!attempts || attempts.length === 0) ? (
                        <Typography style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>No attempt records found.</Typography>
                    ) : (
                        <TableContainer component={Paper} style={{ border: `1px solid ${mediumGrey}`, boxShadow: 'none', maxHeight: '50vh', overflowY: 'auto' }} sx={{ "&::-webkit-scrollbar": { display: 'none' }, scrollbarWidth: "none" }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={headerCellStyle}>Attempt #</TableCell>
                                        <TableCell style={headerCellStyle}>Date & Time</TableCell>
                                        <TableCell style={headerCellStyle}>Score</TableCell>
                                        <TableCell style={headerCellStyle}>Percentage</TableCell>
                                        <TableCell style={headerCellStyle}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {attempts.map((att, index) => {
                                        const attemptNum = attempts.length - index;
                                        const isCompleted = att.status === 'completed';
                                        return (
                                            <TableRow key={att._id || index} hover>
                                                <TableCell style={{ ...bodyCellStyle, fontWeight: 500 }}>{attemptNum}</TableCell>
                                                <TableCell style={bodyCellStyle}>{new Date(att.startTime || att.createdAt).toLocaleString()}</TableCell>
                                                <TableCell style={bodyCellStyle}>
                                                    {isCompleted ? `${att.score ?? 0} / ${att.maxScore ?? 0}` : '-'}
                                                </TableCell>
                                                <TableCell style={bodyCellStyle}>
                                                    {isCompleted ? `${att.percentage ?? 0}%` : '-'}
                                                </TableCell>
                                                <TableCell style={bodyCellStyle}>
                                                    {isCompleted ? (
                                                        <Chip
                                                            label={att.passed ? "Passed" : "Failed"}
                                                            size="small"
                                                            style={{
                                                                backgroundColor: att.passed ? '#e8f5e9' : '#ffebee',
                                                                color: att.passed ? '#2e7d32' : '#c62828',
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label="In Progress"
                                                            size="small"
                                                            style={{
                                                                backgroundColor: '#fff3e0',
                                                                color: '#ef6c00',
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <Box style={{ padding: '1rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button style={{ ...buttonStyle, backgroundColor: red, padding: '0.3rem 1.5rem' }} onClick={onClose}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

const BatchAssessmentResultsDialog = (props) => {
    const { dialogStatus, updateFunc, batchId, batchDetails } = props;
    const dispatch = useDispatch();

    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    const titleStyle = {
        backgroundColor: headingBlue, 
        color: 'white',
        fontSize: '1rem',
        display: "flex",
        alignItems: 'center',
        padding: "0.4rem 1rem",
        justifyContent: 'space-between'
    };

    const headerCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.85rem", 
        fontWeight: "600",
        textAlign: 'center',
        backgroundColor: lightGrey,
        borderBottom: `1px solid ${mediumGrey}`,
        whiteSpace: 'nowrap'
    };

    const bodyCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.85rem", 
        fontWeight: "400",
        textAlign: 'center',
    };

    useEffect(() => {
        if (dialogStatus && batchId) {
            setIsLoading(true);
            
            let options = {
                url: fetchBatchAssessmentResults,
                method: 'GET',
                params: { batchId: batchId },
                headers: {
                    Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
                }
            };

            HttpService(options).then((resp) => {
                const data = resp?.data?.data || resp?.data || [];
                setResults(Array.isArray(data) ? data : []);
                setIsLoading(false);
            }).catch((err) => {
                console.error("Error fetching batch assessment results: ", err);
                dispatch(showSnackBar({ message: 'Error fetching assessment results. Please try again.', severity: 'error' }));
                setIsLoading(false);
            });
        }
    }, [dialogStatus, batchId]);

    const openHistory = (member) => {
        setSelectedMember(member);
        setHistoryOpen(true);
    };

    return (
        <>
            <Dialog maxWidth='lg' fullWidth open={dialogStatus} onClose={() => updateFunc(false)}>
                <DialogTitle style={titleStyle}>
                    <Typography style={{ fontWeight: 600 }}>Batch Assessment Results</Typography>
                    <IconButton onClick={() => updateFunc(false)} style={{ color: 'white', padding: 0 }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {isLoading && <Loader />}
                    <Box style={{ padding: '1.5rem 0rem 0rem 0rem' }}>
                        {batchDetails && (
                            <Box style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '0.4rem', border: `1px solid ${mediumGrey}` }}>
                                <Grid container spacing={2} justifyContent="space-between">
                                    <Grid item xs={4} style={{ wordBreak: 'break-word' }}>
                                        <Typography variant="body2" style={{ color: '#888' }}>Batch Name</Typography>
                                        <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>{batchDetails.batchName || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={4} style={{ wordBreak: 'break-word' }}>
                                        <Typography variant="body2" style={{ color: '#888' }}>Duration</Typography>
                                        <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>
                                            {batchDetails.startDate ? moment(batchDetails.startDate).utcOffset('+05:30').format('DD/MM/YYYY') : '-'} 
                                            {' to '}
                                            {batchDetails.endDate ? moment(batchDetails.endDate).utcOffset('+05:30').format('DD/MM/YYYY') : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4} style={{ wordBreak: 'break-word' }}>
                                        <Typography variant="body2" style={{ color: '#888' }}>Coordinator</Typography>
                                        <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>
                                            {batchDetails.volunteerName || '-'}
                                            {batchDetails.volunteerCN && (
                                                <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 400, marginLeft: '0.4rem' }}>
                                                    - {batchDetails.volunteerCN}
                                                </span>
                                            )}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        
                        <TableContainer component={Paper} style={{ border: `1px solid ${mediumGrey}`, boxShadow: 'none', maxHeight: '55vh', overflowY: 'auto' }} sx={{ "&::-webkit-scrollbar": { display: 'none' }, scrollbarWidth: "none" }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{...headerCellStyle, minWidth: '120px', left: 0, position: 'sticky', zIndex: 3}}>Name</TableCell>
                                        <TableCell style={headerCellStyle}>Contact</TableCell>
                                        <TableCell style={headerCellStyle}>District</TableCell>
                                        <TableCell style={headerCellStyle}>State</TableCell>
                                        <TableCell style={headerCellStyle}>Attempts</TableCell>
                                        <TableCell style={headerCellStyle}>Last Attempt Result</TableCell>
                                        <TableCell style={headerCellStyle}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(isLoading || results.length === 0) ? (
                                        <TableRow>
                                            <TableCell colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                                {!isLoading && "No member records found for this batch."}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        results.map((row, index) => {
                                            let badgeBg = '#eee';
                                            let badgeFg = '#666';
                                            if (row.lastAttemptResult === 'Passed') {
                                                badgeBg = '#e8f5e9';
                                                badgeFg = '#2e7d32';
                                            } else if (row.lastAttemptResult === 'Failed') {
                                                badgeBg = '#ffebee';
                                                badgeFg = '#c62828';
                                            } else if (row.lastAttemptResult === 'In Progress') {
                                                badgeBg = '#fff3e0';
                                                badgeFg = '#ef6c00';
                                            }

                                            return (
                                                <TableRow key={index} hover>
                                                    <TableCell style={{ ...bodyCellStyle, left: 0, position: 'sticky', zIndex: 3, backgroundColor: 'white', textAlign: 'left', fontWeight: 600 }}>{row.name}</TableCell>
                                                    <TableCell style={bodyCellStyle}>{row.contact || '-'}</TableCell>
                                                    <TableCell style={bodyCellStyle}>{row.district || '-'}</TableCell>
                                                    <TableCell style={bodyCellStyle}>{row.state || '-'}</TableCell>
                                                    <TableCell style={{ ...bodyCellStyle, fontWeight: 600 }}>
                                                        {row.attemptsCount}
                                                    </TableCell>
                                                    <TableCell style={bodyCellStyle}>
                                                        <Chip 
                                                            label={row.lastAttemptResult} 
                                                            size="small" 
                                                            style={{
                                                                backgroundColor: badgeBg,
                                                                color: badgeFg,
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell style={bodyCellStyle}>
                                                        <Tooltip title="View Attempt History">
                                                            <IconButton 
                                                                size="small" 
                                                                style={{ color: headingBlue, padding: 0 }}
                                                                onClick={() => openHistory(row)}
                                                            >
                                                                <Visibility fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box style={{ padding: '1rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <Button style={{ ...buttonStyle, backgroundColor: red, padding: '0.3rem 1.5rem' }} onClick={() => updateFunc(false)}>
                                Close
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <MemberHistoryDialog
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                memberName={selectedMember?.name || ''}
                attempts={selectedMember?.attempts || []}
            />
        </>
    );
};

export default BatchAssessmentResultsDialog;
